import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import LoadingSpinner from './LoadingSpinner';
import { useAppSelector } from '../../store/hooks';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary: 'bg-mirsal-green text-mirsal-black hover:bg-mirsal-green-dark focus:ring-mirsal-green',
        admin: 'bg-mirsal-black text-mirsal-white hover:bg-gray-800 focus:ring-gray-500',
        secondary: 'border focus:ring-mirsal-green',
        outline: 'border focus:ring-gray-500',
        ghost: 'focus:ring-gray-500',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
        success: 'bg-mirsal-green text-mirsal-black hover:bg-mirsal-green-dark focus:ring-mirsal-green',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 py-2',
        lg: 'h-12 px-6 py-3 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    loading = false,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props 
  }, ref) => {
    const { theme } = useAppSelector((state) => state.ui);
    
    // Theme-aware styles for outline, ghost, and secondary variants
    const getThemeStyles = () => {
      if (variant === 'outline') {
        return theme === 'dark' 
          ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
          : 'border-gray-300 text-gray-900 bg-transparent hover:bg-gray-50 hover:text-gray-900';
      }
      if (variant === 'ghost') {
        return theme === 'dark' 
          ? 'text-gray-300 hover:bg-gray-800 hover:text-white' 
          : 'text-gray-900 bg-transparent hover:bg-gray-100 hover:text-gray-900';
      }
      if (variant === 'secondary') {
        return theme === 'dark' 
          ? 'bg-gray-800 text-white border-gray-600 hover:bg-gray-700' 
          : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50';
      }
      return '';
    };
    
    return (
      <button
        className={clsx(
          buttonVariants({ variant, size }),
          getThemeStyles(),
          className
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <LoadingSpinner size="sm" className="mr-2" />
        )}
        {!loading && leftIcon && (
          <span className="mr-2">{leftIcon}</span>
        )}
        {children}
        {!loading && rightIcon && (
          <span className="ml-2">{rightIcon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
